import type { ReadonlyVec3 } from 'gl-matrix';
import { useCallback, useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';
import {
    DEFAULT_VOI_EXTENT_FRACTION,
    centerBoundsAt,
    centeredBoundsFraction,
    centeredBoundsMm,
    computeGridAABB,
} from '~/domain/services/imageService/gridGeometry';
import { Grid } from '~/engine/data/common/Grid';
import {
    VoiConsumer,
    voiBoundsState,
    voiConsumerRequestState,
    voiRegionRestrictionState,
} from '~/state/atoms/toolsAtoms';

/**
 * Consumers that request the shared overlay through this hook.
 * Registration is excluded: its activation stays derived from its own
 * tool + checkbox atoms (see {@link voiConsumerRequestState}).
 */
export type VoiRestrictionConsumer = Exclude<VoiConsumer, 'registration'>;

interface UseVoiRegionRestrictionOptions {
    /** Which consumer's record and overlay claim this box belongs to. */
    consumer: VoiRestrictionConsumer;
    /** dicomUid of the selected primary image, or undefined while none. */
    imageUid: string | undefined;
    /** Grid of the selected primary image, or undefined while none. */
    imageGrid: Grid | undefined;
    /**
     * Force the restriction ON for the current image (the SUV-threshold
     * tool, whose box is not an optional restriction but the DOMAIN of
     * SUVmax — no box, no threshold). The difference to the CT tool is
     * exactly this flag, not a fork: the same record, seeding, mirroring
     * and release logic runs; an effect merely re-enables whenever the
     * record reads as off (first open, and after an image switch). The
     * drawn box still persists per image like the optional case.
     */
    mandatory?: boolean;
    /**
     * Seed a FIXED-extent centred box of this many mm per axis (clamped
     * to the image, {@link centeredBoundsMm}) instead of the shared
     * fraction-of-extent default. For consumers whose region is
     * lesion-scaled rather than scan-scaled — the SUV tool's box frames
     * one lesion, and half of a whole-body PET is most of the torso.
     */
    seedExtentMm?: number;
    /**
     * Where a freshly seeded box is centred, in patient mm — normally the
     * crosshair. Without it the box is centred on the IMAGE, which on a
     * whole-body PET puts a lesion-scaled cube in the middle of the
     * abdomen whatever the user was looking at. Clamped into the image, so
     * a crosshair near an edge still yields a box inside the volume.
     */
    seedCenterMm?: ReadonlyVec3;
}

/**
 * "Restrict to region" state for one consumer panel: activation of the
 * shared VOI overlay, the centred default box, and the mirroring between
 * the session-scoped per-consumer record
 * ({@link voiRegionRestrictionState}) and the shared overlay atoms. The
 * panel owns activation and the default; the overlay owns projection and
 * handle editing.
 *
 * WHAT PERSISTS: the on/off flag and the drawn box, for the session,
 * across panel close/reopen and preset-tab switches. WHAT RESETS: both,
 * on a primary-image switch — the record is stamped with the image uid
 * and a record for another image reads as off with no box.
 *
 * Every write here is IDEMPOTENT (same value in ⇒ same state out, no
 * intermediate clear). That is load-bearing, not incidental: the shared
 * CompactToggle used to deliver two onChange calls per click, and an
 * enable path that cleared the bounds before reseeding would have thrown
 * the user's persisted box away on the second one.
 */
export function useVoiRegionRestriction({
    consumer,
    imageUid,
    imageGrid,
    mandatory = false,
    seedExtentMm,
    seedCenterMm,
}: UseVoiRegionRestrictionOptions) {
    const [record, setRecord] = useRecoilState(
        voiRegionRestrictionState(consumer)
    );
    const [voiBounds, setVoiBounds] = useRecoilState(voiBoundsState);
    // Read when a seed actually happens rather than depended on: the
    // crosshair moves on every scroll, and the seed only runs while no
    // box exists.
    const seedCenterRef = useRef(seedCenterMm);
    seedCenterRef.current = seedCenterMm;
    const [voiConsumerRequest, setVoiConsumerRequest] = useRecoilState(
        voiConsumerRequestState
    );

    // A record belonging to another image is inert — never adopted, never
    // mutated in place. It is simply overwritten the first time the user
    // toggles the restriction on this image.
    const isRecordForThisImage =
        imageUid !== undefined && record.imageUid === imageUid;
    const restrictToRegion = isRecordForThisImage && record.enabled;
    const savedBounds = isRecordForThisImage ? record.boundsMm : null;

    const handleRestrictToRegionChange = useCallback(
        (enabled: boolean) => {
            setRecord((prev) => {
                const keepsBox = prev.imageUid === imageUid;
                return {
                    imageUid: imageUid ?? null,
                    enabled,
                    // Disabling KEEPS the box so re-enabling restores it;
                    // only a different image drops it.
                    boundsMm: keepsBox ? prev.boundsMm : null,
                };
            });
        },
        [imageUid, setRecord]
    );

    // Mandatory mode: re-enable whenever the current image's record reads
    // as off — the first open, and after an image switch (the stale
    // record's uid mismatch reads as off, so this also stamps the new
    // image). Routed through the same idempotent handler as the user
    // toggle, so the box-keeping rules are identical.
    useEffect(() => {
        if (mandatory && imageUid !== undefined && !restrictToRegion) {
            handleRestrictToRegionChange(true);
        }
    }, [mandatory, imageUid, restrictToRegion, handleRestrictToRegionChange]);

    // Whether this mount has already handed the overlay its starting
    // region. Per-mount, so a close/reopen starts the count again — which
    // is what makes the mandatory reset below happen once per OPEN rather
    // than once per activation.
    const hasSeededThisOpenRef = useRef(false);

    // Activation mirror: the shared overlay atoms follow the persisted
    // flag, on a toggle AND on the panel remount that a close/reopen or a
    // panel-type switch produces. Restoring the saved box overwrites
    // whatever another consumer left in the shared atom — registration
    // deliberately keeps its box across its own checkbox toggles, and its
    // bounds are the primary image's patient space, not this consumer's.
    //
    // A MANDATORY region does not restore on the first activation of an
    // open: it starts from a fresh seed. It is lesion-scaled and seeded
    // at the crosshair, and the seed only runs when no region exists, so
    // a region kept from an earlier open is one placed for a lesion the
    // user may have navigated away from AND one they cannot re-place by
    // moving the crosshair — the tool would open on the wrong anatomy
    // with no way back to the right one. The optional restriction keeps
    // its box: SR-WOR-H-27-2 promises it persists for the session, and
    // its scan-scaled seed is not tied to where the user is looking.
    useEffect(() => {
        if (restrictToRegion) {
            if (voiConsumerRequest !== consumer) {
                const startFresh = mandatory && !hasSeededThisOpenRef.current;
                hasSeededThisOpenRef.current = true;
                setVoiConsumerRequest(consumer);
                setVoiBounds(startFresh ? null : savedBounds);
            }
        } else if (voiConsumerRequest === consumer) {
            setVoiConsumerRequest(null);
            setVoiBounds(null);
        }
    }, [
        consumer,
        restrictToRegion,
        mandatory,
        savedBounds,
        voiConsumerRequest,
        setVoiConsumerRequest,
        setVoiBounds,
    ]);

    // Seed a centred default whenever the restriction is on but no box
    // exists — first enable on this image, and after an image switch.
    // DEFAULT_VOI_EXTENT_FRACTION of the extent (so the box is visibly
    // smaller than the image and reads as a restriction the moment it
    // appears), unless the consumer asked for
    // a fixed physical extent (`seedExtentMm`). Gated on the shared
    // consumer request being OURS: request and bounds are cleared in the
    // same Recoil batch on release, while the persisted flag can flush
    // separately — without this gate an intermediate flush would re-seed
    // a box the release just cleared, leaving stray bounds for the next
    // consumer.
    useEffect(() => {
        if (
            restrictToRegion &&
            voiConsumerRequest === consumer &&
            voiBounds === null &&
            imageGrid
        ) {
            const aabb = computeGridAABB(imageGrid);
            const seeded =
                seedExtentMm !== undefined
                    ? centeredBoundsMm(aabb, seedExtentMm)
                    : centeredBoundsFraction(aabb, DEFAULT_VOI_EXTENT_FRACTION);
            const center = seedCenterRef.current;
            setVoiBounds(
                center ? centerBoundsAt(seeded, center, aabb) : seeded
            );
        }
    }, [
        consumer,
        restrictToRegion,
        voiConsumerRequest,
        voiBounds,
        imageGrid,
        seedExtentMm,
        setVoiBounds,
    ]);

    // Persist every edit the overlay's drag handles commit, so the box
    // survives an unmount that gives the panel no chance to save (the
    // toolbar-driven panel-type switch, a disabling gate closing the
    // panel). Reference comparison: after the mirror effect restores
    // savedBounds, the shared atom holds the record's own object, and
    // rewriting the record with it would be a pointless write per
    // restore. (The overlay itself no longer writes the shared atom
    // outside a drag — its resize invalidation is a local render tick.)
    useEffect(() => {
        if (
            restrictToRegion &&
            voiConsumerRequest === consumer &&
            voiBounds !== null &&
            voiBounds !== savedBounds
        ) {
            setRecord((prev) => ({ ...prev, boundsMm: voiBounds }));
        }
    }, [
        consumer,
        restrictToRegion,
        voiConsumerRequest,
        voiBounds,
        savedBounds,
        setRecord,
    ]);

    // Deactivate the shared overlay when the panel goes away — through
    // onClose AND through the toolbar-driven panel-type switch and the
    // gating effects that bypass it. Only the SHARED atoms are released:
    // the persisted record is what brings the box back on reopen. Ref so
    // the one-shot unmount effect sees final state.
    const releaseOverlayRef = useRef(() => {});
    releaseOverlayRef.current = () => {
        if (voiConsumerRequest === consumer) {
            setVoiConsumerRequest(null);
            setVoiBounds(null);
        }
    };
    useEffect(() => () => releaseOverlayRef.current(), []);

    /**
     * Drop the drawn region and let the seed put a fresh one where the
     * user is looking now — the crosshair for a lesion-scaled region, the
     * image centre for the scan-scaled one. The way back from a region
     * dragged somewhere unrecoverable, and the only way to re-place a
     * mandatory region without closing the panel.
     *
     * Clears the SHARED bounds only: the persist effect above writes the
     * fresh seed into the record as soon as it lands, so the record is
     * never left holding the discarded region.
     */
    const resetRegion = useCallback(() => {
        setVoiBounds(null);
    }, [setVoiBounds]);

    // The shared overlay atom can hold ANOTHER consumer's box —
    // registration's frames the fit of the fused pair. Surfacing it here
    // would let a caller bound
    // an operation with a region the user drew for something else, so the
    // box is returned only while it is genuinely ours: the restriction is
    // on for THIS consumer's record AND the shared request has already
    // been mirrored to us (it lags the record by one effect tick).
    return {
        restrictToRegion,
        handleRestrictToRegionChange,
        resetRegion,
        voiBounds:
            restrictToRegion && voiConsumerRequest === consumer
                ? voiBounds
                : null,
    };
}
